import type { RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'
import type { BreadcrumbItem } from '@/app/router/meta'

/**
 * The bank module's routes (WP-11, wp/bank §3): paths and names from the route manifest (D-15), `ac` only.
 *
 * - `/ac/bank-accounts` (`bank-accounts`) — the accounts; GoCardless sends the user back here with `?ref=` (§4.1.3).
 * - `/ac/bank-accounts/powens-connect` (`bank-accounts-powens-connect`, OPEN-5 (a), NEW-27: legacy gave the callback
 *   record the same name as the list) — the Powens callback. Powens knows the legacy `/ac/bank-accounts/powens_connect`,
 *   which the generated legacy redirects send straight to the accounts with the query and the hash (manifest override,
 *   queue #38); this record does the same for the new path (§1.1: legacy's own record was a redirect), and the accounts
 *   screen completes the connection when `route.redirectedFrom` is a callback path — a reload of the landed URL posts
 *   nothing (§4.2 check 3).
 * - `/ac/transactions` (`transactions`) — `?account=<id>` presets the Bank Account filter.
 *
 * NEW-28: legacy `meta.module: 'bank'` was never evaluated. The guard checks `resource: 'bank'` (D-21), the permission
 * the API asks for every read of both modules.
 */
const crumb = (text: string, to?: string): BreadcrumbItem => (to ? { text, to } : { text, active: true })

const AccountListView = () => import('./views/AccountListView.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/ac/bank-accounts',
    name: 'bank-accounts',
    component: AccountListView,
    meta: {
      software: 'ac',
      resource: 'bank',
      pageTitle: keys.t('common.nav.accounts'),
      breadcrumb: [crumb(keys.t('bank.route.title.bankAccounts'))],
    },
  },
  {
    path: '/ac/bank-accounts/powens-connect',
    name: 'bank-accounts-powens-connect',
    redirect: (to) => ({ name: 'bank-accounts', query: to.query, hash: to.hash }),
  },
  {
    path: '/ac/transactions',
    name: 'transactions',
    component: () => import('./views/TransactionListView.vue'),
    meta: {
      software: 'ac',
      resource: 'bank',
      pageTitle: keys.t('common.nav.transactions'),
      breadcrumb: [crumb(keys.t('common.nav.bank'), '/ac/bank-accounts'), crumb(keys.t('common.nav.transactions'))],
    },
  },
]

export default routes
